import React from 'react';
import { useFormikContext } from 'formik';
import { PricingInventoryFields } from '../../../components';
import { SectionCard } from '../../../components/SectionCard';
import { ProductType, ProductVariant } from '@shopify-multiseller/graphql/partner/types';
import { IProductInitialValuesProps } from '../types';

export interface ProductPricingInventorySectionProps {
  disabled?: boolean;
  index: number;
  setTrackQuantity: (_value: boolean) => void;
  variants: ProductVariant[];
}

/**
 * What one unit costs and how many there are.
 *
 * Product type used to head this section, because the spike draws it there.
 * It is a different question from price and stock — it decides what kind of
 * thing is being sold, and everything else on the page follows from it — so it
 * now has a panel of its own above this one. On a product with variations this
 * section states why its fields are absent rather than disappearing.
 */
export const ProductPricingInventorySection: React.FC<ProductPricingInventorySectionProps> = ({
  disabled,
  index,
  setTrackQuantity,
  variants,
}) => {
  const { values } = useFormikContext<IProductInitialValuesProps>();
  const currentVariant = variants?.[index];
  const hasOptions = !!values.options && values.options.length > 0;

  return (
    <SectionCard title="Pricing & inventory" testId="product-pricing-section">
      <div className="space-y-5">
        {hasOptions ? (
          /* Plain sentence, not a Note: with product type moved to its own
             panel this is the whole body of the card, and a grey box inside a
             card is two boxes saying one thing. */
          <p className="text-sm text-muted-foreground">
            Because this product has variations, price and quantity are set per variation below.
          </p>
        ) : values.productType ? (
          <PricingInventoryFields
            namePrefix={`variants.${index}`}
            isDigital={values.productType === ProductType.Digital}
            currentQuantity={currentVariant?.quantity || 0}
            disabled={disabled}
            onTrackQuantityChange={setTrackQuantity}
            priceTestId="product-price-input"
            quantityTestId="product-quantity-input"
          />
        ) : (
          /* A product with no type stored — an old or imported listing — drew
             this card as an empty box, which reads as broken rather than as
             waiting for something. What the fields need is stated where the
             answer is, one panel up. */
          <p className="text-sm text-muted-foreground">
            Choose a product type above to set a price and stock level.
          </p>
        )}
      </div>
    </SectionCard>
  );
};
